import { getToken } from '../api.js';

// Document editing tools for the rich-text editor (documents mode,
// migration 036): tables (insert, rows / columns, merge, header row,
// widths, alignment, borders, shading) and pictures (private files of the
// company: <img data-img="12" data-w="50" class="im-center">, shown through
// the API, never a public link).

// ── Pictures ────────────────────────────────────────────────────────────
const cache = new Map(); // id → object URL
export async function imageUrl(id) {
  if (cache.has(id)) return cache.get(id);
  const r = await fetch(`/api/workspace/doc-images/${id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!r.ok) return null;
  const url = URL.createObjectURL(await r.blob());
  cache.set(id, url);
  return url;
}
// Shows the pictures of a box (editor, preview) — their src stays out of the saved text.
export function hydrateImages(root) {
  if (!root) return;
  for (const img of root.querySelectorAll('img[data-img]')) {
    if (img.getAttribute('src')) continue;
    imageUrl(img.dataset.img).then((u) => { if (u) img.src = u; });
  }
}
// The saved text: pictures without their (temporary) src.
export function cleanHtml(root) {
  const c = root.cloneNode(true);
  // Blocks: only their JSON is saved (drawn again from it).
  for (const el of c.querySelectorAll('div.dblock')) { el.innerHTML = ''; el.removeAttribute('contenteditable'); el.removeAttribute('style'); el.className = 'dblock'; }
  for (const img of c.querySelectorAll('img[data-img]')) img.removeAttribute('src');
  for (const el of c.querySelectorAll('.is-selected')) el.classList.remove('is-selected');
  for (const el of c.querySelectorAll('[class=""]')) el.removeAttribute('class');
  return c.innerHTML;
}
// Large photos are shrunk (max 2000 px, JPEG 85 %) before upload; PNG kept
// for small / transparent pictures. Always PNG or JPEG (the PDF formats).
export async function prepareImage(file, { maxSide = 2000, removeWhite = false } = {}) {
  const bmp = await createImageBitmap(file);
  const k = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
  const w = Math.round(bmp.width * k); const h = Math.round(bmp.height * k);
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bmp, 0, 0, w, h);
  if (removeWhite) {
    // "Remove white background" (signatures): near-white pixels become transparent.
    const d = ctx.getImageData(0, 0, w, h);
    for (let i = 0; i < d.data.length; i += 4) {
      const m = Math.min(d.data[i], d.data[i + 1], d.data[i + 2]);
      if (m > 225) d.data[i + 3] = 0; else if (m > 190) d.data[i + 3] = Math.round(d.data[i + 3] * (225 - m) / 35);
    }
    ctx.putImageData(d, 0, 0);
  }
  const png = removeWhite || file.type === 'image/png' && file.size < 1.5 * 1024 * 1024;
  const blob = await new Promise((ok) => canvas.toBlob(ok, png ? 'image/png' : 'image/jpeg', 0.85));
  return new File([blob], png ? 'image.png' : 'image.jpg', { type: png ? 'image/png' : 'image/jpeg' });
}
export async function uploadDocImage(file) {
  const fd = new FormData();
  fd.append('file', await prepareImage(file));
  const r = await fetch('/api/workspace/doc-images', { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(j.error || 'server_error'), { code: j.error || 'server_error' });
  return j;
}
export const imageHtml = (id) => `<p class="ta-center"><img data-img="${Number(id)}" data-w="100" class="im-center" alt=""></p>`;

// ── Tables ──────────────────────────────────────────────────────────────
export function tableHtml(rows, cols, header = true) {
  const w = Math.floor(100 / cols);
  const cell = (tag) => `<${tag} style="width:${w}%"><br></${tag}>`;
  const head = header ? `<thead><tr>${Array.from({ length: cols }, (_, i) => cell('th', i)).join('')}</tr></thead>` : '';
  const body = Array.from({ length: Math.max(1, rows - (header ? 1 : 0)) }, () => `<tr>${Array.from({ length: cols }, (_, i) => `<td${header ? '' : ` style="width:${w}%"`}>${i < 0 ? '' : '<br>'}</td>`).join('')}</tr>`).join('');
  return `<table class="dt">${head}<tbody>${body}</tbody></table><p><br></p>`;
}
// The cell where the caret is.
export function currentCell(box) {
  const sel = window.getSelection();
  if (!sel?.rangeCount) return null;
  const n = sel.getRangeAt(0).startContainer;
  const el = (n.nodeType === 1 ? n : n.parentElement)?.closest('td, th');
  return el && box.contains(el) ? el : null;
}
// The table as a grid (handles colspan / rowspan): grid[r][c] = cell.
function grid(table) {
  const rows = [...table.rows];
  const g = rows.map(() => []);
  rows.forEach((tr, r) => {
    let c = 0;
    for (const cell of tr.cells) {
      while (g[r][c]) c += 1;
      for (let i = 0; i < cell.rowSpan; i++) for (let j = 0; j < cell.colSpan; j++) if (g[r + i]) g[r + i][c + j] = cell;
      c += cell.colSpan;
    }
  });
  return g;
}
const colOf = (g, cell) => { for (const row of g) { const i = row.indexOf(cell); if (i >= 0) return i; } return 0; };
const newCell = (tag, like) => { const c = document.createElement(tag); c.innerHTML = '<br>'; if (like?.style.textAlign) c.style.textAlign = like.style.textAlign; return c; };
export function tableOp(box, op) {
  const cell = currentCell(box);
  const table = cell?.closest('table');
  if (!table) return false;
  const tr = cell.parentElement;
  const g = grid(table);
  const col = colOf(g, cell);
  const ncols = Math.max(...g.map((r) => r.length));
  if (op === 'rowAbove' || op === 'rowBelow') {
    const n = document.createElement('tr');
    const tag = tr.parentElement.tagName === 'THEAD' && op === 'rowAbove' ? 'th' : 'td';
    for (let i = 0; i < ncols; i++) n.appendChild(newCell(tag));
    if (op === 'rowAbove') tr.before(n);
    else if (tr.parentElement.tagName === 'THEAD') (table.tBodies[0] || table.createTBody()).prepend(n);
    else tr.after(n);
  } else if (op === 'colLeft' || op === 'colRight') {
    const at = op === 'colLeft' ? col : col + cell.colSpan;
    [...table.rows].forEach((row, r) => {
      const tag = row.parentElement.tagName === 'THEAD' ? 'th' : 'td';
      const ref = g[r][at];
      const c = newCell(tag);
      if (ref && ref.parentElement === row) ref.before(c); else row.appendChild(c);
    });
    resetWidths(table);
  } else if (op === 'delRow') {
    if (table.rows.length <= 1) { table.remove(); return true; }
    tr.remove();
  } else if (op === 'delCol') {
    if (ncols <= 1) { table.remove(); return true; }
    for (const row of g) { const c = row[col]; if (!c) continue; if (c.colSpan > 1) c.colSpan -= 1; else c.remove(); }
    resetWidths(table);
  } else if (op === 'mergeRight') {
    const right = g[[...table.rows].indexOf(tr)]?.[col + cell.colSpan];
    if (!right || right === cell || right.rowSpan !== cell.rowSpan) return false;
    if (right.textContent.trim()) cell.insertAdjacentHTML('beforeend', ` ${right.innerHTML}`);
    cell.colSpan += right.colSpan; right.remove();
  } else if (op === 'mergeDown') {
    const r = [...table.rows].indexOf(tr) + cell.rowSpan;
    const below = g[r]?.[col];
    if (!below || below.colSpan !== cell.colSpan) return false;
    if (below.textContent.trim()) cell.insertAdjacentHTML('beforeend', `<br>${below.innerHTML}`);
    cell.rowSpan += below.rowSpan; below.remove();
  } else if (op === 'split') {
    // Undo merges of this cell.
    const extra = cell.colSpan - 1;
    for (let i = 0; i < extra; i++) cell.after(newCell(cell.tagName.toLowerCase()));
    cell.colSpan = 1;
    if (cell.rowSpan > 1) {
      const rows = [...table.rows];
      const r0 = rows.indexOf(tr);
      for (let i = 1; i < cell.rowSpan; i++) { const row = rows[r0 + i]; const ref = grid(table)[r0 + i]?.[col + 1]; const c = newCell('td'); if (ref && ref.parentElement === row) ref.before(c); else row?.appendChild(c); }
      cell.rowSpan = 1;
    }
  } else if (op === 'header') {
    if (table.tHead) {
      // Header row back to a normal row.
      const row = table.tHead.rows[0];
      const body = table.tBodies[0] || table.createTBody();
      for (const th of [...row.cells]) { const td = document.createElement('td'); td.innerHTML = th.innerHTML; td.style.cssText = th.style.cssText; td.colSpan = th.colSpan; th.replaceWith(td); }
      body.prepend(row); table.tHead.remove();
    } else {
      const row = table.rows[0];
      const head = table.createTHead();
      for (const td of [...row.cells]) { const th = document.createElement('th'); th.innerHTML = td.innerHTML; th.style.cssText = td.style.cssText; th.colSpan = td.colSpan; td.replaceWith(th); }
      head.appendChild(row);
    }
  } else if (op.startsWith('align-')) {
    cell.style.textAlign = op.slice(6) === 'left' ? '' : op.slice(6);
  } else if (op === 'borders') table.classList.toggle('dt-noborder');
  else if (op === 'shade') table.classList.toggle('dt-noshade');
  else if (op === 'cellShade') cell.style.backgroundColor = cell.style.backgroundColor ? '' : '#fef3c7';
  else if (op === 'delete') table.remove();
  return true;
}
// Equal column widths (after adding / removing a column).
function resetWidths(table) {
  const first = table.rows[0];
  if (!first) return;
  const n = [...first.cells].reduce((s, c) => s + c.colSpan, 0);
  for (const row of table.rows) for (const c of row.cells) c.style.width = '';
  for (const c of first.cells) c.style.width = `${Math.round((100 / n) * c.colSpan)}%`;
}
// Dragging a column border: sets the first row's widths (in %).
export function startColumnDrag(e, box, onDone) {
  const cell = e.target.closest?.('td, th');
  if (!cell || !box.contains(cell)) return false;
  const rect = cell.getBoundingClientRect();
  if (Math.abs(e.clientX - rect.right) > 5) return false;
  const table = cell.closest('table');
  const tw = table.getBoundingClientRect().width;
  const g = grid(table);
  const col = colOf(g, cell) + cell.colSpan - 1;
  const first = g[0];
  const widths = first.map((c, i) => (first.indexOf(c) === i ? c.getBoundingClientRect().width / c.colSpan : null));
  const w0 = first.map((c) => c.getBoundingClientRect().width / c.colSpan);
  const x0 = e.clientX;
  e.preventDefault();
  const move = (ev) => {
    const dx = ev.clientX - x0;
    const a = Math.max(30, w0[col] + dx); const b = Math.max(30, (w0[col + 1] ?? 0) - dx);
    if (w0[col + 1] !== undefined && b <= 30 && dx > 0) return;
    const cur = [...w0]; cur[col] = a; if (cur[col + 1] !== undefined) cur[col + 1] = b;
    const done = new Set();
    first.forEach((c, i) => {
      if (done.has(c)) return; done.add(c);
      const w = cur.slice(i, i + c.colSpan).reduce((s, v) => s + v, 0);
      c.style.width = `${Math.round((w / tw) * 1000) / 10}%`;
    });
  };
  const up = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); onDone(); };
  document.addEventListener('mousemove', move);
  document.addEventListener('mouseup', up);
  void widths;
  return true;
}
export const nearColumnEdge = (e) => {
  const cell = e.target.closest?.('td, th');
  if (!cell) return false;
  return Math.abs(e.clientX - cell.getBoundingClientRect().right) <= 5;
};
